import { yupResolver } from '@hookform/resolvers/yup'
import BackButton from 'components/backButton'
import {
  Button,
  DropdownPortal,
  FormLayout,
  Spinner,
  TextInput,
} from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { RMA_REFUND_TYPES } from 'components/orderManagement'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { CustomObject } from 'types/common'
import { InputOptions } from 'types/input'
import { OrderType } from 'types/order'
import { getRmaConfigurations } from 'utils/api/configApi'
import { createPartialRma, fetchOrder } from 'utils/api/orderApi'
import {
  PAGE_ADMIN_ORDERS,
  PAGE_ADMIN_ORDERS_DETAILS,
  PAGE_NOT_FOUND,
} from 'utils/constants'
import { formatOrderDate } from 'utils/helpers'
import { partialRmaSchema } from 'utils/schema/oderSchema'

interface RmaItem {
  barcode: string
  returnQty: number
  reason?: string
  condition?: string
}

interface PartialRma {
  refundType: string
  products: RmaItem[]
}

const PartialRmaPage = () => {
  const navigate = useNavigate()
  const { orderId } = useParams()

  const [order, setOrder] = useState<OrderType>()

  const [conditions, setConditions] = useState<InputOptions[]>([])
  const [reasons, setReasons] = useState<InputOptions[]>([])

  const [loading, setLoading] = useState<boolean>(true)
  const [generateLoading, setGenerateLoading] = useState<boolean>(false)

  const {
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PartialRma>({
    resolver: yupResolver(partialRmaSchema),
    defaultValues: {
      products: [],
      refundType: 'DEFAULT',
    },
  })

  const handleBack = () => {
    navigate(`${PAGE_ADMIN_ORDERS.path}/${orderId}`)
  }

  const getReasonsAndConditions = async () => {
    try {
      const { data } = await getRmaConfigurations()

      if (data?.rmaConditions?.length > 0) {
        const rmaConditions = data.rmaConditions.map(
          (condition: string): InputOptions => ({
            label: condition,
            value: condition,
            disabled: false,
          }),
        )

        rmaConditions.unshift({
          label: '--SELECT--',
          value: '',
          disabled: false,
        })

        setConditions(rmaConditions)
      }

      if (data?.rmaReasons?.length > 0) {
        const rmaReasons = data?.rmaReasons.map(
          (reason: string): InputOptions => ({
            label: reason,
            value: reason,
            disabled: false,
          }),
        )

        rmaReasons.unshift({
          label: '--SELECT--',
          value: '',
          disabled: false,
        })

        setReasons(rmaReasons)
      }
    } catch (error) {
      setConditions([])
      setReasons([])
    }
  }

  const getOrderDetails = async (orderId: string) => {
    try {
      const { data } = await fetchOrder(orderId)

      if (data?.rma && data.rma?.length > 0 && data.rma[0].isFullRMA) {
        return navigate(`${PAGE_ADMIN_ORDERS_DETAILS.path}/${data.orderNo}`)
      }

      await getReasonsAndConditions()
      setOrder(data)
    } catch (error) {
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (orderId) {
      getOrderDetails(orderId)
    }
  }, [])

  const handlePartialRma = async (data: PartialRma) => {
    if (!order) return
    setGenerateLoading(true)

    const toastId = toast.loading('Generating Rma..')

    try {
      const products = data.products.filter(({ returnQty }) => returnQty !== 0)

      if (products.length === 0) {
        return toast.error(
          'Return quantity must be greater than 0 to proceed with RMA.',
          { id: toastId },
        )
      }

      await createPartialRma(order._id, {
        products,
        refundType: data.refundType,
      })

      navigate(`${PAGE_ADMIN_ORDERS_DETAILS.path}/${order.order_no}`)

      toast.success('RMA Generated Successfully', { id: toastId })
    } catch {
      toast.error('Failed to Generate RMA', { id: toastId })
    } finally {
      setGenerateLoading(false)
    }
  }

  const isRmaGenerated = useMemo(
    () => order?.products?.every(({ qty, returnQty }) => qty - returnQty <= 0),
    [order?.products],
  )

  if (loading) {
    return <Spinner />
  }

  if (!order) {
    return <></>
  }

  return (
    <>
      <Helmet>
        <title>ORMS Generate RMA </title>
      </Helmet>

      <BackButton handleBack={handleBack} />

      {isRmaGenerated && (
        <div className="mb-2 rounded-md bg-yellow-200 px-3 py-2 font-semibold text-slate-900">
          An RMA has already been generated for this order.
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="title">Generate Partial RMA</h3>
          <p className="rounded-lg bg-gray-800 px-3 py-1 text-sm font-semibold text-white shadow">
            {order?.order_no}
          </p>
        </div>

        <FormLayout className="mt-2 rounded-lg border-gray-200 !p-4 text-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <p>
                <span className="font-bold">Store Name:</span>{' '}
                {order?.storeName ?? 'N/A'}
              </p>

              <p>
                <span className="font-bold">Order Date:</span>{' '}
                {order?.order_place_datetime
                  ? formatOrderDate(order?.order_place_datetime)
                  : 'N/A'}
              </p>
              <p>
                <span className="font-bold">Planned Delivery:</span>{' '}
                {order?.Planned_DeliveryDate
                  ? formatOrderDate(order?.Planned_DeliveryDate)
                  : 'N/A'}
              </p>
              <p>
                <span className="font-bold">Delivery Provider:</span>{' '}
                {order?.Delivery_provider ?? 'N/A'}
              </p>
            </div>
          </div>
        </FormLayout>

        <form onSubmit={handleSubmit(handlePartialRma)}>
          {order?.products && order.products?.length > 0 && (
            <Table
              className="mt-3"
              headers={[
                'Barcode',
                'Return Reason',
                'Return Condition',
                'Return Quantity',
                'Ordered Quantity',
              ]}
            >
              {order.products.map((product: CustomObject, index: number) => {
                const isReturned =
                  Number(product.qty) - Number(product.returnQty) <= 0

                if (!isReturned) {
                  setValue(`products.${index}.barcode`, product.barcode, {
                    shouldValidate: false,
                    shouldDirty: false,
                  })
                }

                return (
                  <TableRow key={product._id}>
                    <TableCell>
                      {product?.description && (
                        <p className="mb-1 font-bold">{product.description}</p>
                      )}
                      <p>
                        <strong>SKU:</strong> {product.barcode}
                      </p>
                      <p>
                        <strong>UOM:</strong> {product?.uom || 'NA'}
                      </p>
                    </TableCell>

                    <TableCell>
                      {!isReturned ? (
                        <DropdownPortal
                          options={reasons}
                          placeholder="Select a reason"
                          name={`products.${index}.reason`}
                          setValue={setValue}
                          error={
                            errors?.products?.[index]?.reason?.message as string
                          }
                        />
                      ) : (
                        'NA'
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Condition */}
                      {!isReturned ? (
                        <DropdownPortal
                          parentClassName="!static"
                          options={conditions}
                          placeholder="Select the condition"
                          name={`products.${index}.condition`}
                          setValue={setValue}
                          error={
                            errors?.products?.[index]?.condition
                              ?.message as string
                          }
                        />
                      ) : (
                        'NA'
                      )}
                    </TableCell>
                    <TableCell>
                      {!isReturned ? (
                        <TextInput
                          disabled={generateLoading}
                          parentClassName="max-w-40"
                          type="number"
                          name={`products.${index}.returnQty`}
                          register={register}
                          defaultValue={0}
                          max={product.qty - product.returnQty}
                          min={0}
                          step="any"
                          error={
                            errors?.products?.[index]?.returnQty
                              ?.message as string
                          }
                        />
                      ) : (
                        product.returnQty
                      )}
                    </TableCell>
                    <TableCell>{product.qty}</TableCell>
                  </TableRow>
                )
              })}
            </Table>
          )}

          {/* Cash Refund */}

          {!isRmaGenerated && (
            <>
              {/* Refund Type */}
              <div className="mt-4">
                <span className="text-sm font-semibold text-sidebar-primary">
                  Refund Type
                </span>

                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {RMA_REFUND_TYPES.map(({ label, value }) => (
                    <label
                      className="flex items-center gap-2 text-sm"
                      key={value}
                    >
                      <input
                        type="radio"
                        value={value}
                        {...register('refundType', { required: true })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-3">
                <Button
                  buttonText="Generate"
                  className="w-full max-w-36"
                  loading={generateLoading}
                />
              </div>
            </>
          )}
        </form>
      </div>
    </>
  )
}

export default PartialRmaPage
